import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  UsersFour, MagnifyingGlass, PencilSimple, Trash, Lock, LockOpen, Key,
  Users, Student, X as CloseIcon, Check, Warning, IdentificationCard,
  EnvelopeSimple, Phone, User as UserIcon, Circle, TrendUp, Coins,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";

// ==== helper: format CPF ====
function fmtCpf(d) {
  const s = (d || "").replace(/\D/g, "");
  if (s.length !== 11) return d || "—";
  return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(9)}`;
}
function fmtCnpj(d) {
  const s = (d || "").replace(/\D/g, "");
  if (s.length !== 14) return d || "—";
  return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8, 12)}-${s.slice(12)}`;
}
function fmtDoc(d) {
  const s = (d || "").replace(/\D/g, "");
  if (s.length === 11) return fmtCpf(s);
  if (s.length === 14) return fmtCnpj(s);
  return d || "—";
}

export default function AdminUsers() {
  const [tab, setTab] = useState("clients"); // "clients" | "members"
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("created_at"); // "created_at" | "revenue" | "commission"
  const [data, setData] = useState({ users: [], members: [], total: 0, total_all: 0, blocked: 0 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited
  const [confirm, setConfirm] = useState(null); // { type: 'delete' | 'bulk', ids, name }
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const fullList = tab === "clients" ? data.users : data.members;
  const totalPages = Math.max(1, Math.ceil(fullList.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const list = fullList.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  const brl = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(n || 0));

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "clients") {
        const params = new URLSearchParams({
          q, per_page: "200", include_metrics: "true", sort,
        });
        const { data: d } = await api.get(`/admin/users?${params}`);
        setData({ users: d.users, members: [], total: d.total, total_all: d.total_all, blocked: d.blocked });
      } else {
        const { data: d } = await api.get(`/admin/members?q=${encodeURIComponent(q)}&per_page=200`);
        setData({ users: [], members: d.members, total: d.total, total_all: d.total_all, blocked: d.blocked });
      }
    } catch (e) {
      toast.error(errMsg(e, "Erro ao carregar"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, sort]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Reset seleção e página ao trocar aba/sort/busca
  useEffect(() => {
    setSelected(new Set());
    setPage(1);
  }, [tab, sort, q]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };

  const toggleSelectAllVisible = () => {
    const allSelected = list.length > 0 && list.every((r) => selected.has(r.id));
    setSelected((prev) => {
      const nx = new Set(prev);
      if (allSelected) list.forEach((r) => nx.delete(r.id));
      else list.forEach((r) => nx.add(r.id));
      return nx;
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    setConfirm(null);
    if (ids.length === 0) return;
    const endpoint = tab === "clients" ? "/admin/users" : "/admin/members";
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await api.delete(`${endpoint}/${id}`);
        ok++;
      } catch { fail++; }
    }
    setSelected(new Set());
    if (fail === 0) toast.success(`${ok} ${ok === 1 ? "registro excluído" : "registros excluídos"}`);
    else toast.warning(`${ok} excluído(s), ${fail} falharam`);
    await load();
  };

  const toggleBlock = async (row) => {
    try {
      const endpoint = tab === "clients"
        ? `/admin/users/${row.id}/toggle-block`
        : `/admin/members/${row.id}/toggle-block`;
      const { data: d } = await api.post(endpoint);
      toast.success(d.blocked ? "Usuário bloqueado" : "Usuário desbloqueado");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Erro"));
    }
  };

  const remove = async () => {
    const row = confirm;
    setConfirm(null);
    try {
      const endpoint = tab === "clients"
        ? `/admin/users/${row.id}`
        : `/admin/members/${row.id}`;
      await api.delete(endpoint);
      toast.success("Excluído com sucesso");
      setSelected((prev) => { const nx = new Set(prev); nx.delete(row.id); return nx; });
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Erro"));
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const endpoint = tab === "clients"
        ? `/admin/users/${editing.id}`
        : `/admin/members/${editing.id}`;
      const body = tab === "clients"
        ? { name: editing.name, email: editing.email, phone: editing.phone, cpf_cnpj: editing.cpf_cnpj }
        : { name: editing.name, email: editing.email, phone: editing.phone, cpf: editing.cpf };
      await api.put(endpoint, body);
      toast.success("Atualizado com sucesso");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao salvar"));
    }
  };

  const resetPassword = async (row) => {
    const newPw = prompt(`Nova senha para ${row.name} (${row.email})\nMínimo 6 caracteres:`);
    if (!newPw) return;
    if (newPw.length < 6) { toast.error("Senha muito curta"); return; }
    if (!confirm(`Confirma redefinir a senha de ${row.email} para "${newPw}"?\n\nO ${tab === 'clients' ? 'cliente' : 'aluno'} receberá um email com a nova senha.`)) return;
    try {
      const endpoint = tab === "clients"
        ? `/admin/users/${row.id}/reset-password`
        : `/admin/members/${row.id}/reset-password`;
      await api.post(endpoint, { new_password: newPw });
      toast.success(`Senha redefinida! Nova senha: ${newPw}`);
    } catch (e) {
      toast.error(errMsg(e, "Erro ao redefinir senha"));
    }
  };

  const total = data.total_all;
  const blocked = data.blocked;
  const active = total - blocked;

  return (
    <div className="fadeUp max-w-6xl" data-testid="admin-users-page">
      <div className="label-caps mb-2 flex items-center gap-1"><UsersFour size={12} /> Gestão de contas</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-8">
        Usuários da plataforma
      </h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6" data-testid="user-tabs">
        <button
          onClick={() => setTab("clients")}
          data-testid="tab-clients"
          className={`px-4 py-2.5 rounded-sm text-sm font-bold flex items-center gap-2 transition ${
            tab === "clients"
              ? "bg-[var(--accent)] text-black"
              : "border border-[var(--border)] text-white/60 hover:text-white"
          }`}
        >
          <Users size={16} weight="bold" /> Clientes
        </button>
        <button
          onClick={() => setTab("members")}
          data-testid="tab-members"
          className={`px-4 py-2.5 rounded-sm text-sm font-bold flex items-center gap-2 transition ${
            tab === "members"
              ? "bg-[var(--accent)] text-black"
              : "border border-[var(--border)] text-white/60 hover:text-white"
          }`}
        >
          <Student size={16} weight="bold" /> Membros (alunos)
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-4">
        <div className="card-flat p-4">
          <div className="label-caps text-white/50">Total</div>
          <div className="font-display font-black text-3xl mt-1" data-testid="stat-total">{total}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-caps text-[var(--success)]">Ativos</div>
          <div className="font-display font-black text-3xl mt-1 text-[var(--success)]" data-testid="stat-active">{active}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-caps text-[var(--danger)]">Bloqueados</div>
          <div className="font-display font-black text-3xl mt-1 text-[var(--danger)]" data-testid="stat-blocked">{blocked}</div>
        </div>
      </div>

      {tab === "clients" && list.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6">
          <div className="card-flat p-4 border-l-2 border-l-[var(--success)]">
            <div className="label-caps text-white/50 flex items-center gap-1"><TrendUp size={11} /> Receita total (top 100)</div>
            <div className="font-display font-black text-2xl mt-1 text-[var(--success)]" data-testid="stat-revenue-total">
              {brl(list.reduce((s, u) => s + (u.revenue || 0), 0))}
            </div>
          </div>
          <div className="card-flat p-4 border-l-2 border-l-[var(--accent)]">
            <div className="label-caps text-white/50 flex items-center gap-1"><Coins size={11} /> Comissão gerada</div>
            <div className="font-display font-black text-2xl mt-1 text-[var(--accent)]" data-testid="stat-commission-total">
              {brl(list.reduce((s, u) => s + (u.commission || 0), 0))}
            </div>
          </div>
        </div>
      )}

      {/* Search + Sort */}
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, e-mail, telefone ou CPF"
            data-testid="user-search"
            className="w-full pl-10 pr-4 py-3 bg-[var(--bg-base)] border border-[var(--border)] rounded-sm text-sm focus:border-[var(--accent)] outline-none"
          />
        </div>
        {tab === "clients" && (
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="user-sort"
            className="md:w-56 bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 text-sm focus:border-[var(--accent)] outline-none"
          >
            <option value="created_at">Mais recentes</option>
            <option value="revenue">💰 Melhor cliente (receita)</option>
            <option value="commission">🏆 Mais comissão paga</option>
          </select>
        )}
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div
          className="mb-3 px-4 py-3 rounded-sm border border-[var(--accent)] bg-[var(--accent)]/10 flex items-center justify-between gap-3 fadeUp"
          data-testid="bulk-bar"
        >
          <div className="text-sm">
            <b data-testid="bulk-count">{selected.size}</b>{" "}
            {selected.size === 1 ? "selecionado" : "selecionados"}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              data-testid="bulk-clear"
              className="text-[11px] px-3 py-1.5 rounded-sm border border-white/10 hover:bg-white/5"
            >
              Limpar seleção
            </button>
            <button
              onClick={() => setConfirm({ type: "bulk", count: selected.size })}
              data-testid="bulk-delete"
              className="text-[11px] px-3 py-1.5 rounded-sm bg-[var(--danger)] text-white font-bold flex items-center gap-1.5 hover:brightness-110"
            >
              <Trash size={11} weight="bold" /> Excluir selecionados
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-left">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={list.length > 0 && list.every((r) => selected.has(r.id))}
                    onChange={toggleSelectAllVisible}
                    data-testid="select-all"
                    className="accent-[var(--accent)] w-4 h-4 cursor-pointer"
                    aria-label="Selecionar todos visíveis"
                  />
                </th>
                <th className="px-4 py-3 label-caps text-white/50">Usuário</th>
                <th className="px-4 py-3 label-caps text-white/50 hidden md:table-cell">Contato</th>
                <th className="px-4 py-3 label-caps text-white/50">CPF{tab === "clients" ? "/CNPJ" : ""}</th>
                {tab === "clients" && (
                  <>
                    <th className="px-4 py-3 label-caps text-white/50 hidden lg:table-cell text-right">
                      <span className="inline-flex items-center gap-1"><TrendUp size={11} /> Receita</span>
                    </th>
                    <th className="px-4 py-3 label-caps text-white/50 hidden lg:table-cell text-right">
                      <span className="inline-flex items-center gap-1"><Coins size={11} /> Comissão</span>
                    </th>
                  </>
                )}
                <th className="px-4 py-3 label-caps text-white/50 hidden md:table-cell">Status</th>
                <th className="px-4 py-3 label-caps text-white/50 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={tab === "clients" ? 8 : 6} className="px-4 py-10 text-center text-white/40">Carregando...</td></tr>
              )}
              {!loading && list.length === 0 && (
                <tr><td colSpan={tab === "clients" ? 8 : 6} className="px-4 py-10 text-center text-white/40">
                  Nenhum {tab === "clients" ? "cliente" : "membro"} encontrado.
                </td></tr>
              )}
              {!loading && list.map((row) => {
                const doc = tab === "clients" ? row.cpf_cnpj : row.cpf;
                const isBlocked = !!row.blocked;
                const isSelected = selected.has(row.id);
                return (
                  <tr
                    key={row.id}
                    className={`border-b border-white/5 ${
                      isSelected ? "bg-[var(--accent)]/5" : "hover:bg-white/[0.02]"
                    }`}
                    data-testid={`row-${row.id}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(row.id)}
                        data-testid={`select-${row.id}`}
                        className="accent-[var(--accent)] w-4 h-4 cursor-pointer"
                        aria-label={`Selecionar ${row.name}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          isBlocked ? "bg-[var(--danger)]/20 text-[var(--danger)]" : "bg-[var(--accent)]/20 text-[var(--accent)]"
                        }`}>
                          <UserIcon size={14} weight="bold" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold truncate">{row.name || "—"}</div>
                          <div className="text-[10px] text-white/40 truncate md:hidden">{row.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="text-white/80 truncate flex items-center gap-1.5"><EnvelopeSimple size={11} /> {row.email}</div>
                      {row.phone && <div className="text-white/50 text-xs mt-0.5 flex items-center gap-1.5"><Phone size={10} /> {row.phone}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs" data-testid={`cpf-${row.id}`}>
                      {fmtDoc(doc)}
                    </td>
                    {tab === "clients" && (
                      <>
                        <td className="px-4 py-3 hidden lg:table-cell text-right" data-testid={`revenue-${row.id}`}>
                          <div className="font-bold text-[var(--success)]">{brl(row.revenue || 0)}</div>
                          {(row.tx_count || 0) > 0 && (
                            <div className="text-[10px] text-white/40">{row.tx_count} tx</div>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-right" data-testid={`commission-${row.id}`}>
                          <div className="font-bold text-[var(--accent)]">{brl(row.commission || 0)}</div>
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 hidden md:table-cell">
                      {isBlocked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[var(--danger)]/15 text-[var(--danger)] text-[10px] font-bold uppercase tracking-wider">
                          <Circle size={6} weight="fill" /> Bloqueado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[var(--success)]/15 text-[var(--success)] text-[10px] font-bold uppercase tracking-wider">
                          <Circle size={6} weight="fill" /> Ativo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditing({ ...row })}
                          data-testid={`edit-${row.id}`}
                          className="p-2 rounded-sm border border-white/10 hover:border-[var(--accent)] hover:text-[var(--accent)] transition"
                          title="Editar"
                        >
                          <PencilSimple size={13} />
                        </button>
                        <button
                          onClick={() => toggleBlock(row)}
                          data-testid={`block-${row.id}`}
                          className={`p-2 rounded-sm border border-white/10 transition ${
                            isBlocked
                              ? "hover:border-[var(--success)] hover:text-[var(--success)]"
                              : "hover:border-yellow-400 hover:text-yellow-400"
                          }`}
                          title={isBlocked ? "Desbloquear" : "Bloquear"}
                        >
                          {isBlocked ? <LockOpen size={13} /> : <Lock size={13} />}
                        </button>
                        <button
                          onClick={() => resetPassword(row)}
                          data-testid={`reset-pw-${row.id}`}
                          className="p-2 rounded-sm border border-white/10 hover:border-sky-400 hover:text-sky-400 transition"
                          title="Redefinir senha"
                        >
                          <Key size={13} />
                        </button>
                        <button
                          onClick={() => setConfirm({ type: "delete", id: row.id, name: row.name })}
                          data-testid={`delete-${row.id}`}
                          className="p-2 rounded-sm border border-white/10 hover:border-[var(--danger)] hover:text-[var(--danger)] transition"
                          title="Excluir"
                        >
                          <Trash size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {fullList.length > PER_PAGE && (
        <div className="flex items-center justify-between mt-4 text-xs text-white/60" data-testid="pagination">
          <div>
            Mostrando <b className="text-white">{(currentPage - 1) * PER_PAGE + 1}</b>–
            <b className="text-white">{Math.min(currentPage * PER_PAGE, fullList.length)}</b> de{" "}
            <b className="text-white">{fullList.length}</b>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={currentPage === 1}
              data-testid="page-first"
              className="px-2.5 py-1.5 rounded-sm border border-white/10 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              «
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="page-prev"
              className="px-2.5 py-1.5 rounded-sm border border-white/10 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ‹
            </button>
            <span className="px-3 py-1.5 font-mono">
              <b className="text-[var(--accent)]">{currentPage}</b> / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="page-next"
              className="px-2.5 py-1.5 rounded-sm border border-white/10 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ›
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={currentPage === totalPages}
              data-testid="page-last"
              className="px-2.5 py-1.5 rounded-sm border border-white/10 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {editing && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setEditing(null)}
          data-testid="edit-modal"
        >
          <div
            className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-6 max-w-md w-full fadeUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="label-caps">Editar {tab === "clients" ? "cliente" : "membro"}</div>
                <div className="font-display font-bold text-xl">{editing.name || editing.email}</div>
              </div>
              <button onClick={() => setEditing(null)} className="btn-ghost !p-1.5"><CloseIcon size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label-caps block mb-1">Nome</label>
                <input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  data-testid="edit-name"
                  className="w-full bg-[var(--bg-surface)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm focus:border-[var(--accent)] outline-none" />
              </div>
              <div>
                <label className="label-caps block mb-1">E-mail</label>
                <input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  data-testid="edit-email"
                  className="w-full bg-[var(--bg-surface)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm focus:border-[var(--accent)] outline-none" />
              </div>
              <div>
                <label className="label-caps block mb-1">Telefone</label>
                <input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                  data-testid="edit-phone"
                  className="w-full bg-[var(--bg-surface)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm focus:border-[var(--accent)] outline-none" />
              </div>
              <div>
                <label className="label-caps block mb-1">CPF{tab === "clients" ? "/CNPJ" : ""}</label>
                <input
                  value={tab === "clients" ? (editing.cpf_cnpj || "") : (editing.cpf || "")}
                  onChange={(e) => setEditing(
                    tab === "clients"
                      ? { ...editing, cpf_cnpj: e.target.value }
                      : { ...editing, cpf: e.target.value }
                  )}
                  data-testid="edit-cpf"
                  className="w-full bg-[var(--bg-surface)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm font-mono focus:border-[var(--accent)] outline-none"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-6">
              <button onClick={() => setEditing(null)} data-testid="edit-cancel"
                className="text-xs px-3 py-2 rounded-sm border border-white/10 hover:bg-white/5">
                Cancelar
              </button>
              <button onClick={saveEdit} data-testid="edit-save"
                className="text-xs px-4 py-2 rounded-sm bg-[var(--accent)] text-black font-bold flex items-center gap-1.5">
                <Check size={12} weight="bold" /> Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirm Delete */}
      {confirm && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setConfirm(null)}
          data-testid="confirm-modal"
        >
          <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-6 max-w-sm w-full fadeUp"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[var(--danger)]/20 flex items-center justify-center">
                <Warning size={18} weight="fill" color="var(--danger)" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-base">
                  {confirm.type === "bulk"
                    ? `Excluir ${confirm.count} selecionado${confirm.count === 1 ? "" : "s"}?`
                    : "Excluir permanentemente?"}
                </div>
                {confirm.type !== "bulk" && (
                  <div className="text-xs text-white/50 truncate">{confirm.name}</div>
                )}
              </div>
            </div>
            <p className="text-sm text-white/70 mb-5">
              {confirm.type === "bulk"
                ? `Todos os ${confirm.count} registros marcados serão apagados permanentemente. Essa ação não pode ser desfeita.`
                : "Essa ação apaga o registro do banco de dados e não pode ser desfeita."}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirm(null)} data-testid="confirm-cancel"
                className="text-xs px-3 py-2 rounded-sm border border-white/10 hover:bg-white/5">
                Cancelar
              </button>
              <button
                onClick={confirm.type === "bulk" ? bulkDelete : remove}
                data-testid="confirm-delete"
                className="text-xs px-3 py-2 rounded-sm bg-[var(--danger)] text-white font-bold flex items-center gap-1.5"
              >
                <Trash size={12} weight="bold" /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
